/**
 * Analytics for the account portal: Mixpanel product analytics plus the
 * marketing tags (GA4, Google Ads, LinkedIn Insight, HubSpot) with page views
 * on client-side navigation.
 *
 * Mixpanel boots in every environment and on every route, opted out by
 * default outside production. The marketing tags are production only and
 * scoped to TRACKED_PATHS: they load the first time the visit reaches a
 * tracked path and page views fire only for tracked paths, so login, recovery
 * and settings traffic never reaches a marketing vendor. URLs reported to Google
 * keep campaign params only (FORWARDED_QUERY_PARAMS).
 */
import { userTracking } from '@revelio/core';

import { isEducationalEmail } from './app/utils/educationalEmail';
import { signupAttribution } from './app/utils/first-party-attribution';
import { isE2eTestSignupEmail } from './app/utils/is-e2e-test-signup-email';
import { environment } from './environment';

type LinkedInTracker = ((action: string, data?: unknown) => void) & {
  q: unknown[][];
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _hsq?: unknown[][];
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: LinkedInTracker;
  }
}

type TrackingEnv = {
  PUBLIC_production: boolean;
  PUBLIC_mixpanelToken: string;
  PUBLIC_GA4_MEASUREMENT_ID?: string;
  PUBLIC_GOOGLE_ADS_ID?: string;
  PUBLIC_LINKEDIN_PARTNER_ID?: string;
  PUBLIC_HUBSPOT_PORTAL_ID?: string;
};

const TRACKED_PATHS = ['/signup', '/onboarding', '/completed', '/personalize'];

// GA4/Ads session stitching across the marketing site -> portal hop.
const CROSS_DOMAINS = ['reveliolabs.com', 'account.reveliolabs.com'];

// _ga / _gcl cookie lifetime: 2 years.
const COOKIE_EXPIRES_SECONDS = 63072000;

const FORWARDED_QUERY_PARAMS = /^(utm_[a-z]+|gclid|src|s)$/;

const TRIAL_LEAD_VALUES = { commercial: 32000, academic: 17000 } as const;

const LEAD_MARKER_PREFIX = 'trial-lead:';

const LINKEDIN_TRIAL_CONVERSION_ID = 31652049;

let initialized = false;
let tagsLoaded = false;
let trackingEnv: TrackingEnv | undefined;
let hubspotTrackedInitialView = false;
const trackedLeads = new Set<string>();

function isTrackedPath(path: string): boolean {
  return TRACKED_PATHS.includes(path.replace(/\/$/, '') || '/');
}

function injectSrcScript(src: string, attrs: Record<string, string> = {}) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  for (const [name, value] of Object.entries(attrs)) {
    script.setAttribute(name, value);
  }
  document.head.appendChild(script);
}

// gtag auto-collects the full page URL on every hit, so configs and events
// override page_location with this sanitized version.
function sanitizedLocation(): string {
  const url = new URL(window.location.href);
  const kept = new URLSearchParams();
  for (const [name, value] of url.searchParams) {
    if (FORWARDED_QUERY_PARAMS.test(name)) {
      kept.append(name, value);
    }
  }
  const query = kept.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
}

function initGoogleTags(ga4Id?: string, adsId?: string) {
  if (!ga4Id && !adsId) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // biome-ignore lint/complexity/noArguments: gtag.js requires the Arguments object; plain arrays are ignored
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('set', 'linker', {
    domains: CROSS_DOMAINS,
    accept_incoming: true,
  });
  if (ga4Id) {
    // Page views come from trackPageView, never automatically.
    window.gtag('config', ga4Id, {
      send_page_view: false,
      cookie_expires: COOKIE_EXPIRES_SECONDS,
      page_location: sanitizedLocation(),
    });
  }
  if (adsId) {
    window.gtag('config', adsId, {
      cookie_expires: COOKIE_EXPIRES_SECONDS,
      page_location: sanitizedLocation(),
    });
  }
  injectSrcScript(
    `https://www.googletagmanager.com/gtag/js?id=${ga4Id ?? adsId}`
  );
}

function initLinkedInInsight(partnerId?: string) {
  if (!partnerId) return;
  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);
  if (!window.lintrk) {
    window.lintrk = Object.assign(
      (action: string, data?: unknown) => {
        window.lintrk?.q.push([action, data]);
      },
      { q: [] as unknown[][] }
    );
  }
  injectSrcScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

function initHubSpot(portalId?: string) {
  if (!portalId) return;
  window._hsq = window._hsq || [];
  // The HubSpot script records the landing view itself. Seeding the path
  // before the loader keeps the raw URL out of that view.
  window._hsq.push(['setPath', window.location.pathname]);
  injectSrcScript(`https://js.hs-scripts.com/${portalId}.js`, {
    id: 'hs-script-loader',
  });
}

function loadMarketingTags(path: string) {
  if (tagsLoaded || !trackingEnv?.PUBLIC_production || !isTrackedPath(path)) {
    return;
  }
  tagsLoaded = true;
  initGoogleTags(
    trackingEnv.PUBLIC_GA4_MEASUREMENT_ID,
    trackingEnv.PUBLIC_GOOGLE_ADS_ID
  );
  initLinkedInInsight(trackingEnv.PUBLIC_LINKEDIN_PARTNER_ID);
  initHubSpot(trackingEnv.PUBLIC_HUBSPOT_PORTAL_ID);
}

/** Boots Mixpanel everywhere and the marketing tags on a tracked production path. */
export function initTracking(env: TrackingEnv = environment) {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  userTracking.init(env.PUBLIC_mixpanelToken, {
    debug: !env.PUBLIC_production,
    opt_out_tracking_by_default: !env.PUBLIC_production,
  });

  trackingEnv = env;
  loadMarketingTags(window.location.pathname);
}

function leadTracked(accountId: string): boolean {
  if (trackedLeads.has(accountId)) return true;
  try {
    return localStorage.getItem(LEAD_MARKER_PREFIX + accountId) !== null;
  } catch {
    return false;
  }
}

function persistLead(accountId: string) {
  try {
    localStorage.setItem(LEAD_MARKER_PREFIX + accountId, String(Date.now()));
  } catch {
    return;
  }
}

export function trackTrialLead(email: string, accountId: string) {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (isE2eTestSignupEmail(email) || leadTracked(accountId)) return;
  trackedLeads.add(accountId);
  const segment = isEducationalEmail(email) ? 'academic' : 'commercial';
  const params: Record<string, string | number | (() => void)> = {
    currency: 'USD',
    value: TRIAL_LEAD_VALUES[segment],
    segment,
    form_name: 'account_signup',
    event_callback: () => persistLead(accountId),
  };
  const domain = email.split('@')[1]?.toLowerCase();
  if (domain) params.email_domain = domain;
  const attribution = signupAttribution();
  const firstTouchChannel = attribution?.first_touch?.channel;
  if (typeof firstTouchChannel === 'string') {
    params.first_touch_channel = firstTouchChannel;
  }
  if (attribution?.rl_aid) params.rl_aid = attribution.rl_aid;
  window.gtag('event', 'generate_lead', params);
  window.lintrk?.('track', { conversion_id: LINKEDIN_TRIAL_CONVERSION_ID });
}

/** Emits a GA4 + HubSpot page view for a tracked client-side navigation. */
export function trackPageView(path: string) {
  if (typeof window === 'undefined' || !isTrackedPath(path)) return;
  loadMarketingTags(path);
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: sanitizedLocation(),
  });
  if (window._hsq) {
    window._hsq.push(['setPath', path]);
    // The loader records the first (landing) view itself; explicit views are
    // only for later route changes.
    if (hubspotTrackedInitialView) {
      window._hsq.push(['trackPageView']);
    } else {
      hubspotTrackedInitialView = true;
    }
  }
}
